// Readiness belongs to the current rendered viewport's BASE sources, never the station catalog, occupancy or prices.
// The spinner is only for a missing basemap: the first style and tiles, the view a search/session applies
// (plus a style switch, which replaces every tile). Ordinary pan/zoom never shows it, and it appears only
// after SHOW_DELAY ms of continuous waiting. Slow loading is never an error: the retry notice needs a fault
// that does not heal by itself (see RETRY_DELAYS and the stall limit).
export const SHOW_DELAY = 400;
// MapLibre never fetches a failed tile, style, sprite, glyph range or TileJSON again. A failed basemap tile is
// refreshed after these delays, any other basemap error reloads the style (`reload`, the Retry action) after
// them; only what still fails then counts as broken. Cancelled requests (zoom/pan) are no failures.
export const RETRY_DELAYS = [1000, 2000, 4000, 8000];
export function mapLoading(map, changed, {timers = globalThis, delay = SHOW_DELAY, initialDelay = delay, retries = RETRY_DELAYS, stall = 45000, network = globalThis, reload = null} = {}) {
  let waitingStyle = false, styleReady = false, phase = 'loading', generation = 0, deadline, stopped = false;
  let blocking = true, hadReady = false, tileFailure = false, candidate = null, searching = false;
  let held = 0, showTimer = null, due = false, wait = initialDelay, reloads = 0, reloadTimer = null;
  const failed = new Map();
  const overlay = id => id === 'sites' || id === 'site-pins';
  const sources = () => Object.keys(map.getStyle()?.sources || {}).filter(id => !overlay(id));
  const ready = () => !held && styleReady && !map.isMoving() && sources().every(id => {
    try { return map.isSourceLoaded(id); } catch { return false; }
  });
  const broken = () => tileFailure || [...failed.values()].some(tile => tile.exhausted);
  // The camera flight of a search is not missing content: its wait counts from the flight's end.
  const needed = () => !stopped && phase === 'loading' && blocking && !(searching && map.isMoving());
  function report() {
    if (!needed()) { timers.clearTimeout(showTimer); showTimer = null; due = false; }
    else if (!due && showTimer === null) {
      showTimer = timers.setTimeout(() => { showTimer = null; due = true; report(); }, wait);
      wait = delay;
    }
    changed({phase, visible: needed() && due, blocking, generation});
  }
  // No basemap progress at all for `stall` ms is a hang (a request that never answers), not a slow link.
  function watch() {
    timers.clearTimeout(deadline);
    const token = generation;
    deadline = timers.setTimeout(() => { if (token === generation && phase === 'loading') fail(); }, stall);
  }
  function begin(reason = 'view') {
    if (stopped) return;
    generation++; candidate = null;
    blocking = blocking || !hadReady || ['search', 'style', 'style-request'].includes(reason);
    if (reason !== 'view') searching = reason === 'search';
    if (reason === 'style' || reason === 'style-request') {
      waitingStyle = reason === 'style-request'; styleReady = false; tileFailure = false; forget(); timers.clearTimeout(reloadTimer);
    } else again(true);
    phase = 'loading'; report(); watch();
    map.triggerRepaint();
  }
  // While held the view cannot count as ready: a search holds it until its camera is issued, so the old view's tiles do not release the pins early.
  function hold() {
    let open = true; held++;
    return () => { if (open) { open = false; held--; map.triggerRepaint(); } };
  }
  function fail(latch = false) { searching = false; tileFailure ||= latch; timers.clearTimeout(deadline); phase = 'error'; blocking = false; report(); }
  function rendered() {
    if (stopped || broken() || !ready()) { candidate = null; return; }
    // A second rendered frame lets viewport/source recalculation settle after a cached move.
    if (phase !== 'ready' && candidate !== generation) { candidate = generation; map.triggerRepaint(); return; }
    if (phase !== 'ready') { timers.clearTimeout(deadline); hadReady = true; reloads = 0; phase = 'ready'; blocking = false; searching = false; report(); }
  }
  const key = (sourceId, {z, x, y}) => `${sourceId}/${z}/${x}/${y}`;
  function refresh(tile) { try { map.refreshTiles(tile.sourceId, [tile.id]); } catch { failed.delete(tile.key); } }
  function forget() { for (const tile of failed.values()) timers.clearTimeout(tile.timer); failed.clear(); }
  // A move or a regained connection asks every given-up tile once more; one that left the view never answers.
  function again(last) {
    for (const tile of failed.values()) if (tile.exhausted) {
      tile.exhausted = false; tile.attempts = last ? retries.length : 0; refresh(tile);
    }
  }
  // Style, sprite, glyphs or TileJSON: only a style reload fetches them again.
  function sourceError() {
    if (!reload || reloads >= retries.length) return fail(true);
    tileFailure = true; timers.clearTimeout(reloadTimer);
    reloadTimer = timers.setTimeout(() => { reloads++; reload(); }, retries[reloads]);
  }
  function tileError(event) {
    const id = event.tile?.tileID?.canonical;
    if (!id) return sourceError();
    if (event.error?.name === 'AbortError') return;
    const tile = failed.get(key(event.sourceId, id)) || {key: key(event.sourceId, id), sourceId: event.sourceId, id: {z: id.z, x: id.x, y: id.y}, attempts: 0};
    failed.set(tile.key, tile); timers.clearTimeout(tile.timer); watch();
    if (tile.attempts >= retries.length) { tile.exhausted = true; return fail(); }
    tile.timer = timers.setTimeout(() => { tile.attempts++; refresh(tile); }, retries[tile.attempts]);
  }
  function tileData(event) {
    const id = event.tile?.tileID?.canonical;
    if (!id || !event.sourceId || overlay(event.sourceId)) return;
    if (phase === 'loading') watch();
    const tile = failed.get(key(event.sourceId, id));
    if (!tile) return;
    timers.clearTimeout(tile.timer); failed.delete(tile.key);
    if (phase === 'error' && !broken()) { phase = 'loading'; report(); map.triggerRepaint(); }
  }
  // Back online: given-up tiles are asked again; any other failure (source, style JSON, hang) reloads the style.
  const online = () => {
    if (stopped) return;
    const tiles = [...failed.values()].some(tile => tile.exhausted);
    if (reload && (tileFailure || (phase === 'error' && !tiles))) { reloads = 0; timers.clearTimeout(reloadTimer); reload(); }
    else { again(false); map.triggerRepaint(); }
  };
  const events = {
    'style.load': () => { if (!waitingStyle) { styleReady = true; map.triggerRepaint(); } },
    'movestart': () => begin(), 'resize': () => begin(),
    'moveend': () => { map.triggerRepaint(); report(); }, 'render': rendered, 'data': tileData,
    'dataloading': event => { if (event.sourceId && !overlay(event.sourceId) && phase === 'ready') begin(); },
    // Basemap downloads only: a tile, a source, or a request error (MapLibre's AJAXError carries a status, 0 offline).
    // Logic errors such as a duplicate image name are no missing basemap.
    'error': event => { if (!waitingStyle && !overlay(event.sourceId) && (event.tile || event.sourceId || Number.isInteger(event.error?.status))) tileError(event); }
  };
  for (const [event, handler] of Object.entries(events)) map.on(event, handler);
  network.addEventListener?.('online', online);
  begin('initial');
  // fail(): the app could not fetch the style JSON; it takes the same reload backoff as a source error.
  return {begin, hold, fail: sourceError, get phase() { return phase; }, get visible() { return needed() && due; },
    stop() {
      stopped = true; timers.clearTimeout(showTimer); timers.clearTimeout(deadline); timers.clearTimeout(reloadTimer); forget();
      network.removeEventListener?.('online', online);
      for (const [event, handler] of Object.entries(events)) map.off(event, handler);
    }};
}

// DOM view of the basemap state: spinner, retry notice, busy flag. Returns whether pins may show
// (false while a blocking load runs, so pins never float over a blank or stale basemap).
export function basemapView(state, ready, doc = document) {
  const map = doc.getElementById('map');
  map.dataset.basemapState = state.phase; map.setAttribute('aria-busy', String(state.phase === 'loading'));
  doc.getElementById('map-loading').hidden = !state.visible; doc.getElementById('map-retry').hidden = state.phase !== 'error';
  if (state.blocking) ready = state.phase !== 'loading';
  if (state.phase !== 'loading') ready = true;
  map.toggleAttribute('data-basemap-pending', !ready);
  return ready;
}
